import { type ReactNode, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import i18n, { isSupportedLanguage, LANGUAGE_PARAM } from "../i18n/i18n";
import { backendService } from "../services/backend.service";
import { getAnalyticsIdentity, trackErrorEvent } from "../utils/analytics";
import { type UserInfo, useAuth } from "./AuthProvider";

const ASSUTA_DOMAIN = "assuta.co.il";

/** The signed-in user's saved language, or null while nobody is signed in. */
const savedLanguageFor = (userInfo: UserInfo): string | null => {
	if (!getAnalyticsIdentity(userInfo)) return null;
	// DEFAULT: an account that never picked a language reads English — except Assuta accounts, which
	// are Hebrew-speaking. The same rule App applied before the sync moved here.
	return userInfo.language || (userInfo.username?.endsWith(ASSUTA_DOMAIN) ? "he" : "en");
};

/**
 * The one owner of the UI language. Precedence runs one way:
 *
 * 1. Signed in, the SAVED preference (`userInfo.language`) is the language.
 * 2. `?lang=xx` is a one-shot request to change that preference — the same thing the header language
 *    menu does: it is saved to the account, then shown, and the parameter is removed from the URL so a
 *    reload or a later language-menu change can't be pulled back by it.
 * 3. Signed out there is no preference to write, so `?lang=` only changes what this session shows; the
 *    saved preference takes over at sign-in.
 *
 * Effects use the module `i18n` singleton, never the hook's: react-i18next returns a NEW wrapper object
 * every time the language changes, so an effect keyed on it re-fires on every change. Two such effects
 * (the URL and the saved preference) used to undo each other forever — "Maximum update depth exceeded".
 */
export const I18nProvider = ({ children }: { children: ReactNode }) => {
	// Subscribes this provider to language changes, so `dir` follows them.
	const { i18n: i18nForRender } = useTranslation();
	const dir = i18nForRender.dir();
	const [searchParams, setSearchParams] = useSearchParams();
	const urlLanguage = searchParams.get(LANGUAGE_PARAM);
	const { userInfo, updateUserInfo } = useAuth();
	const savedLanguage = savedLanguageFor(userInfo);
	// Set while a `?lang=` choice is being saved, so the old saved value can't pull the page back mid-save.
	const pendingUrlLanguage = useRef<string | null>(null);
	// The `?lang=` value already acted on, so a re-render before the URL is cleaned can't save it twice.
	const handledUrlLanguage = useRef<string | null>(null);

	useEffect(() => {
		document.body.dir = dir;
	}, [dir]);

	// Declared before the saved-preference effect: when both run in one commit (signing in with `?lang=`
	// in the URL), the pending flag must be up before the saved value is applied.
	useEffect(() => {
		if (urlLanguage === null) {
			handledUrlLanguage.current = null;
			return;
		}
		if (handledUrlLanguage.current === urlLanguage) return;
		if (!isSupportedLanguage(urlLanguage)) {
			handledUrlLanguage.current = urlLanguage;
			trackErrorEvent("language_url_param_unsupported", `Unsupported ?${LANGUAGE_PARAM}= value`, { language: urlLanguage });
			return;
		}
		if (i18n.language !== urlLanguage) void i18n.changeLanguage(urlLanguage);
		if (savedLanguage === null) return;

		handledUrlLanguage.current = urlLanguage;
		setSearchParams(
			(params) => {
				params.delete(LANGUAGE_PARAM);
				return params;
			},
			{ replace: true },
		);
		if (savedLanguage === urlLanguage) return;

		pendingUrlLanguage.current = urlLanguage;
		backendService
			.updateUserLanguage(urlLanguage)
			.then(() => updateUserInfo())
			.catch((error: unknown) => {
				// The account still holds the old language, so show that rather than a choice that didn't stick.
				trackErrorEvent("language_url_param_save_failed", error, { language: urlLanguage });
				void i18n.changeLanguage(savedLanguage);
			})
			.finally(() => {
				pendingUrlLanguage.current = null;
			});
	}, [urlLanguage, savedLanguage, setSearchParams, updateUserInfo]);

	useEffect(() => {
		if (savedLanguage === null || pendingUrlLanguage.current !== null) return;
		if (i18n.language !== savedLanguage) void i18n.changeLanguage(savedLanguage);
	}, [savedLanguage]);

	return <>{children}</>;
};
